import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ApplicationButton,
  ApplicationPage,
  ApplicationSectionHeader,
  ApplicationStateRegion,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../../components/application";
import {
  archiveActivity,
  listAuthoringActivities,
} from "../services/activityAuthoringApi";

function ActivityCard({ activity, onAction }) {
  const personal = activity.visibility === "only_you";
  const capeVr = activity.authoringMethod?.key === "cape_vr";
  return (
    <ApplicationSurface className="av-activity-library__card" variant="subtle">
      <div className="av-activity-library__card-heading">
        <div>
          <h3>{activity.name}</h3>
          {activity.description ? <p>{activity.description}</p> : null}
        </div>
        {!personal ? <div className="av-activity-library__badges"><span>Provided by All-Voiced</span></div> : null}
      </div>
      <p><strong>Activity type:</strong> {capeVr ? "CAPE-Vr" : "Perceptual activity"}</p>
      {personal && activity.selectedSampleCount != null ? <p>{activity.selectedSampleCount} selected samples</p> : null}
      {personal ? <p className="av-activity-library__lifecycle"><strong>{activity.lifecycle === "ready_to_assign" ? "Ready to assign" : "Draft — not ready to assign yet"}</strong> <span>Private</span></p> : null}
      {personal ? (
        <div className="av-activity-library__actions">
          {activity.actions?.canView ? <ApplicationButton size="compact" variant="secondary" onClick={() => onAction("view", activity)}>View activity</ApplicationButton> : null}
          {activity.actions?.canEdit ? <ApplicationButton size="compact" variant="secondary" onClick={() => onAction("edit", activity)}>Continue editing</ApplicationButton> : null}
          {activity.actions?.canArchive ? <ApplicationButton size="compact" variant="quiet" onClick={() => onAction("archive", activity)}>Archive</ApplicationButton> : null}
        </div>
      ) : <div className="av-activity-library__actions"><ApplicationButton size="compact" variant="secondary" onClick={() => onAction("view", activity)}>View activity</ApplicationButton></div>}
    </ApplicationSurface>
  );
}

export default function ActivityLibraryPage() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setActivities(await listAuthoringActivities());
      setError("");
    } catch {
      setError("Activities could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleAction(action, activity) {
    try {
      if (action === "view") return navigate(`/app/facilitate/activities/${activity.id}`);
      if (action === "edit") return navigate(`/app/facilitate/activities/${activity.id}/edit`);
      if (action === "archive" && window.confirm(`Archive “${activity.name}”? Existing assignments keep their saved activity evidence.`)) {
        await archiveActivity(activity.id);
        await load();
      }
    } catch {
      setError("That activity action could not be completed. Refresh and try again.");
    }
  }

  const system = activities.filter(activity => activity.classification === "governed_system");
  const personal = activities.filter(activity => activity.classification?.startsWith("personal_"));

  return (
    <ApplicationPage className="av-activity-library" variant="workspace">
      <ApplicationViewHeader contextLabel="Facilitate · Activities" title="Activities" description="Create reusable activities, then choose them when you create an assignment." />
      <div className="av-activity-library__intro-actions">
        <ApplicationButton variant="quiet" onClick={() => navigate("/app/facilitate")}>Back to Facilitate</ApplicationButton>
        <ApplicationButton onClick={() => navigate("/app/facilitate/activities/new")}>Create activity</ApplicationButton>
      </div>
      {error ? <ApplicationStateRegion status="error" message={error} /> : null}
      {loading ? <ApplicationStateRegion status="loading" message="Loading activities…" /> : (
        <>
          <section aria-labelledby="ready-made-activities-heading">
            <ApplicationSectionHeader title="Ready-made activities" titleId="ready-made-activities-heading" description="Activities provided by All-Voiced, ready to use and not editable." />
            <div className="av-activity-library__grid">{system.map(activity => <ActivityCard key={activity.id} activity={activity} onAction={handleAction} />)}</div>
            {!system.length ? <ApplicationStateRegion status="empty" message="No system activities are available right now." /> : null}
          </section>
          <section aria-labelledby="my-activities-heading">
            <ApplicationSectionHeader title="My activities" titleId="my-activities-heading" />
            <div className="av-activity-library__grid">{personal.map(activity => <ActivityCard key={activity.id} activity={activity} onAction={handleAction} />)}</div>
            {!personal.length ? <ApplicationSurface className="av-activity-library__empty"><ApplicationStateRegion status="empty" message="You haven't created an activity yet." detail="Build a perceptual activity or use the CAPE-Vr protocol." /><ApplicationButton onClick={() => navigate("/app/facilitate/activities/new")}>Create activity</ApplicationButton></ApplicationSurface> : null}
          </section>
        </>
      )}
    </ApplicationPage>
  );
}
